import { useState } from 'react';
import { Paper, Stack, Group, Text, ActionIcon, Collapse, Badge, TextInput, Button, Menu } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconEdit, IconTrash, IconPlus, IconCopy, IconDots, IconGripVertical } from '@tabler/icons-react';
import { ActivityList } from './ActivityList';
import { ActivityFormModal } from './ActivityFormModal';
import { addActivity, deleteDay, updateDay, copyDay } from '../../../utils/programHelpers';
import type { IDay, IProgram, IActivity } from '@ironlogic4/shared/types/programs';
import type { ActivityTemplate } from '@ironlogic4/shared/types/activityTemplates';
import type { BenchmarkTemplate } from '@ironlogic4/shared/types/benchmarkTemplates';

interface DayItemProps {
  day: IDay;
  weekId: string;
  program: IProgram;
  onProgramChange: (program: IProgram) => void;
  onProgramChangeWithAutoSave?: (program: IProgram) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  templateMap: Record<string, ActivityTemplate>;
  templates: ActivityTemplate[];
  benchmarkTemplates: BenchmarkTemplate[];
  weightBenchmarkOptions: Array<{ value: string; label: string }>;
  distanceBenchmarkOptions: Array<{ value: string; label: string }>;
  timeBenchmarkOptions: Array<{ value: string; label: string }>;
  dragHandleProps?: any;
}

export function DayItem({ day, program, onProgramChange, onProgramChangeWithAutoSave, isExpanded, onToggleExpanded, templateMap, templates, benchmarkTemplates, weightBenchmarkOptions, distanceBenchmarkOptions, timeBenchmarkOptions, dragHandleProps }: DayItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(day.name);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);

  const handleSaveName = () => {
    if (editValue.trim()) {
      const updated = updateDay(program, day.id, (d) => {
        d.name = editValue.trim();
      });
      if (onProgramChangeWithAutoSave) {
        onProgramChangeWithAutoSave(updated);
      } else {
        onProgramChange(updated);
      }
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditValue(day.name);
    setIsEditing(false);
  };

  const handleAddActivity = (activity: Omit<IActivity, 'id' | 'order'>) => {
    const updated = addActivity(program, day.id, activity);

    // Use auto-save callback if provided, otherwise use regular callback
    if (onProgramChangeWithAutoSave) {
      onProgramChangeWithAutoSave(updated);
    } else {
      onProgramChange(updated);
    }
  };

  const handleDelete = () => {
    if (confirm(`Delete day "${day.name}"? This will delete all activities.`)) {
      const updated = deleteDay(program, day.id);
      if (onProgramChangeWithAutoSave) {
        onProgramChangeWithAutoSave(updated);
      } else {
        onProgramChange(updated);
      }
    }
  };

  const handleCopy = () => {
    const updated = copyDay(program, day.id);
    if (onProgramChangeWithAutoSave) {
      onProgramChangeWithAutoSave(updated);
    } else {
      onProgramChange(updated);
    }
  };

  return (
    <>
      <Paper
        withBorder
        p="md"
        style={{
          borderLeft: '1px solid #e9ecef',
        }}
      >
        <Stack gap="md">
          {/* Day Header */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" style={{ flex: 1 }}>
              {dragHandleProps && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  style={{ cursor: 'grab' }}
                  {...dragHandleProps}
                >
                  <IconGripVertical size={18} />
                </ActionIcon>
              )}
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={onToggleExpanded}
              >
                {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              </ActionIcon>
              {isEditing ? (
                <Group gap="xs">
                  <TextInput
                    value={editValue}
                    onChange={(e) => setEditValue(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    size="sm"
                    style={{ width: 160 }}
                    autoFocus
                  />
                  <Button size="xs" onClick={handleSaveName}>Save</Button>
                  <Button size="xs" variant="subtle" onClick={handleCancelEdit}>Cancel</Button>
                </Group>
              ) : (
                <>
                  <Text fw={500} size="sm">
                    {day.name}
                  </Text>
                  <ActionIcon variant="subtle" size="xs" onClick={() => setIsEditing(true)}>
                    <IconEdit size={10} />
                  </ActionIcon>
                </>
              )}
              <Badge size="xs" variant="light" color="gray">
                {day.activities.length} {day.activities.length === 1 ? 'activity' : 'activities'}
              </Badge>
            </Group>

            <Menu withinPortal position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm">
                  <IconDots size={12} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconPlus size={10} />} onClick={() => setIsActivityModalOpen(true)}>
                  Add Activity
                </Menu.Item>
                <Menu.Item leftSection={<IconCopy size={10} />} onClick={handleCopy}>
                  Copy Day
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item leftSection={<IconTrash size={10} />} color="red" onClick={handleDelete}>
                  Delete Day
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Activity List */}
          <Collapse in={isExpanded}>
            <Stack gap="md">
              <div style={{ paddingLeft: 20 }}>
                <ActivityList
                  dayId={day.id}
                  activities={day.activities}
                  program={program}
                  onProgramChange={onProgramChange}
                  onProgramChangeWithAutoSave={onProgramChangeWithAutoSave}
                  templateMap={templateMap}
                  templates={templates}
                  benchmarkTemplates={benchmarkTemplates}
                  weightBenchmarkOptions={weightBenchmarkOptions}
                  distanceBenchmarkOptions={distanceBenchmarkOptions}
                  timeBenchmarkOptions={timeBenchmarkOptions}
                />
              </div>

              {/* Add Activity Button */}
              <Button
                variant="light"
                size="sm"
                leftSection={<IconPlus size={14} />}
                onClick={() => setIsActivityModalOpen(true)}
                fullWidth
              >
                Add Activity
              </Button>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      <ActivityFormModal
        opened={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
        onSubmit={handleAddActivity}
        templates={templates}
        benchmarkTemplates={benchmarkTemplates}
        weightBenchmarkOptions={weightBenchmarkOptions}
        distanceBenchmarkOptions={distanceBenchmarkOptions}
        timeBenchmarkOptions={timeBenchmarkOptions}
      />
    </>
  );
}